"use client";

import type { FC } from "react";
import { useState } from "react";
import { Checkbox, TableCell, TableRow } from "@akashnetwork/ui/components";
import { cn } from "@akashnetwork/ui/utils";
import Link from "next/link";
import { DeploymentProjectChip } from "@src/components/projects/DeploymentProjectChip/DeploymentProjectChip";
import { useDeploymentStatus } from "@src/hooks/useDeploymentStatus/useDeploymentStatus";
import type { ListedDeploymentDto } from "@src/types/deployment";
import type { ApiProviderList } from "@src/types/provider";
import { UrlService } from "@src/utils/urlUtils";
import { resolveDeploymentGpus } from "../DeploymentDetail/DeploymentPlacements/placementModel";
import { DeploymentStatusBadge } from "../DeploymentDetail/DeploymentStatusBadge";
import { DeploymentRecoveryAction } from "../DeploymentRecoveryAction/DeploymentRecoveryAction";
import { DeploymentActionsMenu } from "./DeploymentActionsMenu";
import { DeploymentBadges } from "./DeploymentBadges";
import { DeploymentEndpoints, DeploymentEndpointsPanel } from "./DeploymentEndpoints";
import { DeploymentSpecSummary } from "./DeploymentSpecSummary";
import { useDeploymentReachability } from "./useDeploymentReachability";
export const DEPENDENCIES = {
  useDeploymentReachability,
  useDeploymentStatus,
  DeploymentStatusBadge,
  DeploymentRecoveryAction,
  DeploymentBadges,
  DeploymentEndpoints,
  DeploymentEndpointsPanel,
  DeploymentSpecSummary,
  DeploymentActionsMenu,
  DeploymentProjectChip
};

/** TableCell zeroes its right padding for any cell holding a checkbox, which would pin these controls to the table's edge. */
const CONTROLS_CELL = "pl-0 [&:has([role=checkbox])]:pr-4";
const COLUMN_COUNT = 5;
export interface DeploymentRowProps {
  deployment: ListedDeploymentDto;
  providers: ApiProviderList[] | undefined;
  isSelectable?: boolean;
  isSelected?: boolean;
  isClosing?: boolean;
  canManage?: boolean;
  onSelect?: (input: {
    id: string;
    isShiftPressed: boolean;
  }) => void;
  onDeploymentClosed?: () => void;
  dependencies?: typeof DEPENDENCIES;
}
export const DeploymentRow: FC<DeploymentRowProps> = ({
  deployment,
  providers,
  isSelectable,
  isSelected,
  isClosing = false,
  canManage = true,
  onSelect,
  onDeploymentClosed,
  dependencies: d = DEPENDENCIES
}) => {
  const {
    leases,
    workload,
    endpoints,
    isLoadingEndpoints,
    unreachableReason
  } = d.useDeploymentReachability({
    deployment,
    providers
  });
  const status = d.useDeploymentStatus({
    dseq: deployment.dseq,
    state: deployment.state,
    leases,
    workload
  });
  const [isShowingEndpoints, setIsShowingEndpoints] = useState(false);
  const toggleEndpoints = () => setIsShowingEndpoints(current => !current);
  /** Endpoints can drop below the count that draws the toggle while the panel is open, which would strand it with nothing left to close it. */
  const isShowingEndpointPanel = isShowingEndpoints && endpoints.length > 1;
  return <>
      <TableRow className={cn(isShowingEndpointPanel && "border-b-0")} data-sentry-element="TableRow" data-sentry-source-file="DeploymentRow.tsx">
        <TableCell data-sentry-element="TableCell" data-sentry-source-file="DeploymentRow.tsx">
          <div className="flex flex-wrap items-center gap-1.5">
            <d.DeploymentStatusBadge status={status} leases={leases} isSummarized data-sentry-element="d.DeploymentStatusBadge" data-sentry-source-file="DeploymentRow.tsx" />
            <d.DeploymentBadges deployment={deployment} data-sentry-element="d.DeploymentBadges" data-sentry-source-file="DeploymentRow.tsx" />
          </div>
        </TableCell>
        <TableCell data-sentry-element="TableCell" data-sentry-source-file="DeploymentRow.tsx">
          <Link href={UrlService.deploymentDetails(deployment.dseq)} className="block truncate font-semibold hover:underline" data-sentry-element="Link" data-sentry-source-file="DeploymentRow.tsx">
            {deployment.name || `Deployment #${deployment.dseq}`}
          </Link>
          <d.DeploymentProjectChip projectId={deployment.projectId} data-sentry-element="d.DeploymentProjectChip" data-sentry-source-file="DeploymentRow.tsx" />
        </TableCell>
        <TableCell data-sentry-element="TableCell" data-sentry-source-file="DeploymentRow.tsx">
          {status.launch?.phase === "failed" && canManage ? <d.DeploymentRecoveryAction dseq={deployment.dseq} recovery={status.launch.recovery} leases={leases ?? []} isCompact /> : <d.DeploymentEndpoints endpoints={endpoints} isLoading={isLoadingEndpoints} unreachableReason={unreachableReason} isSpinningUp={status.isSpinningUp} isExpanded={isShowingEndpointPanel} onToggleExpanded={toggleEndpoints} />}
        </TableCell>
        <TableCell data-sentry-element="TableCell" data-sentry-source-file="DeploymentRow.tsx">
          <d.DeploymentSpecSummary deployment={deployment} resolvedGpus={resolveDeploymentGpus(deployment.leases)} layout="columns" data-sentry-element="d.DeploymentSpecSummary" data-sentry-source-file="DeploymentRow.tsx" />
        </TableCell>
        <TableCell className={CONTROLS_CELL} data-sentry-element="TableCell" data-sentry-source-file="DeploymentRow.tsx">
          <div className="flex items-center justify-end gap-1">
            {isSelectable && !isClosing && <Checkbox aria-label={`Select deployment ${deployment.name || deployment.dseq}`} checked={isSelected} expandedTouchTarget className="shrink-0" onClick={event => onSelect?.({
            id: deployment.dseq,
            isShiftPressed: event.shiftKey
          })} />}
            <d.DeploymentActionsMenu deployment={deployment} onDeploymentClosed={onDeploymentClosed} isClosing={isClosing} canManage={canManage} data-sentry-element="d.DeploymentActionsMenu" data-sentry-source-file="DeploymentRow.tsx" />
          </div>
        </TableCell>
      </TableRow>

      {isShowingEndpointPanel && <TableRow className="hover:bg-transparent">
          <TableCell colSpan={COLUMN_COUNT} className="px-4 pb-4 pt-0">
            <d.DeploymentEndpointsPanel endpoints={endpoints} />
          </TableCell>
        </TableRow>}
    </>;
};